import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  FileImage, FileText, AlertCircle, ShieldCheck, ShieldAlert, Download, Loader2, Link2, Check, ChevronDown,
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { ZoomableImage } from "@/components/ui/ZoomableImage";
import { supabase } from "@/integrations/supabase/client";
import { StatusBadge, type ExtractionRow } from "./DocumentHistory";
import { toast } from "sonner";

const BOL_LABELS: Record<string, string> = {
  bol_number: "BOL Number",
  trailer_id: "Trailer ID",
  timestamp: "Timestamp",
  shipper_name: "Shipper",
  shipper_address: "Shipper Address",
  consignee_name: "Consignee",
  consignee_address: "Consignee Address",
  carrier_name: "Carrier",
  seal_number: "Seal #",
  po_number: "PO Number",
  weight: "Weight",
  pieces: "Pieces",
  commodity_description: "Commodity",
  special_instructions: "Special Instructions",
};

const TENDER_LABELS: Record<string, string> = {
  order_number: "Route / Order / Load #",
  origin_timestamp: "Origin Timestamp",
  destination_timestamp: "Destination Timestamp",
  origin_location: "Origin Location",
  destination_location: "Destination Location",
  trailer_number: "Trailer #",
  flat_rate: "Flat Rate / Linehaul",
  fuel_surcharge: "Fuel Surcharge",
  total_rate: "Total Rate / Cost",
  carrier_name: "Carrier",
  shipper_name: "Shipper",
  commodity: "Commodity",
  weight: "Weight",
  special_instructions: "Special Instructions",
};

function isPdf(path: string) {
  return path.toLowerCase().endsWith(".pdf");
}

function isImage(path: string) {
  return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(path);
}

function DocumentPreview({ filePath }: { filePath: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.storage
        .from("idp-uploads")
        .createSignedUrl(filePath, 3600);
      if (cancelled || error || !data?.signedUrl) {
        if (!cancelled) { setBlobUrl(null); setSignedUrl(null); setLoading(false); }
        return;
      }
      if (!cancelled) setSignedUrl(data.signedUrl);
      try {
        const resp = await fetch(data.signedUrl);
        const blob = await resp.blob();
        if (!cancelled) {
          setBlobUrl(URL.createObjectURL(blob));
          setLoading(false);
        }
      } catch {
        if (!cancelled) { setBlobUrl(null); setLoading(false); }
      }
    })();
    return () => {
      cancelled = true;
      setBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [filePath]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 bg-muted/30 rounded-lg">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!blobUrl) {
    return (
      <div className="flex items-center justify-center py-8 bg-muted/30 rounded-lg text-sm text-muted-foreground">
        Unable to load preview
      </div>
    );
  }

  if (isPdf(filePath)) {
    const viewUrl = signedUrl || blobUrl;
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4 bg-muted/30 rounded-lg border">
        <FileImage className="h-16 w-16 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">PDF preview is not supported in this view</p>
        <div className="flex gap-2">
          <Button variant="default" size="sm" asChild>
            <a href={viewUrl!} target="_blank" rel="noopener noreferrer">
              <FileText className="h-4 w-4 mr-2" /> View PDF
            </a>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href={blobUrl!} download={filePath.split("/").pop()}>
              <Download className="h-4 w-4 mr-2" /> Download
            </a>
          </Button>
        </div>
      </div>
    );
  }

  if (isImage(filePath)) {
    return (
      <div className="rounded-lg overflow-hidden border bg-muted/30">
        <ZoomableImage
          src={blobUrl}
          openUrl={signedUrl || blobUrl}
          downloadUrl={blobUrl || signedUrl || undefined}
          fileName={filePath.split("/").pop() || "document"}
          alt="Document preview"
          className="h-[50vh] w-full"
        />
      </div>
    );
  }

  // Fallback for other file types
  return (
    <div className="flex flex-col items-center justify-center py-8 gap-3 bg-muted/30 rounded-lg">
      <FileText className="h-12 w-12 text-muted-foreground" />
      <Button asChild variant="outline" size="sm">
        <a href={blobUrl} download>
          <Download className="h-4 w-4 mr-2" /> Download to View
        </a>
      </Button>
    </div>
  );
}

function AttachToOrderButton({ row, onAttached }: { row: ExtractionRow; onAttached: () => void }) {
  const [open, setOpen] = useState(false);
  const [attaching, setAttaching] = useState(false);

  const { data: orders } = useQuery({
    queryKey: ["orders-for-attach"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("id, unique_id, ship_from_city, ship_to_city")
        .neq("status", "canceled")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  const handleAttach = async (orderId: string) => {
    setAttaching(true);
    try {
      const table = row.document_type === "tender" ? "order_tender_images" : "order_bol_images";
      const { error } = await supabase.from(table).insert({
        order_id: orderId,
        image_url: row.file_path,
        image_path: row.file_path,
      });
      if (error) throw error;
      toast.success("Document attached to order");
      setOpen(false);
      onAttached();
    } catch (e: any) {
      toast.error("Failed to attach: " + (e.message || "Unknown error"));
    } finally {
      setAttaching(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Link2 className="h-4 w-4" />
          Attach to Order
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search orders..." />
          <CommandList>
            <CommandEmpty>No orders found.</CommandEmpty>
            <CommandGroup>
              {(orders || []).map((o) => (
                <CommandItem
                  key={o.id}
                  value={`${o.unique_id} ${o.ship_from_city} ${o.ship_to_city}`}
                  onSelect={() => handleAttach(o.id)}
                  disabled={attaching}
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium text-sm">{o.unique_id}</span>
                    <span className="text-xs text-muted-foreground">
                      {o.ship_from_city} → {o.ship_to_city}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface Props {
  row: ExtractionRow | null;
  isAttached?: boolean;
  attachedOrderId?: string | null;
  onClose: () => void;
}

export function DocumentDetailDialog({ row, isAttached, attachedOrderId, onClose }: Props) {
  const queryClient = useQueryClient();
  if (!row) return null;

  const labels = row.document_type === "tender" ? TENDER_LABELS : BOL_LABELS;
  const displayData = row.verified_data || row.extracted_data || {};
  const confidence = (displayData as any).confidence_score ?? 0;

  return (
    <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {row.document_type === "tender" ? <FileText className="h-5 w-5" /> : <FileImage className="h-5 w-5" />}
            {row.file_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap text-sm text-muted-foreground">
            <Badge variant="outline" className="gap-1">
              {row.document_type === "tender" ? "Tender" : "BOL"}
            </Badge>
            <StatusBadge status={row.status} isVerified={row.is_verified} isAttached={isAttached} attachedOrderId={attachedOrderId} />
            {row.is_verified ? (
              <Badge variant="info" className="gap-1">
                <ShieldCheck className="h-3 w-3" /> Verified
              </Badge>
            ) : row.status === "completed" ? (
              <Badge variant="warning" className="gap-1">
                <ShieldAlert className="h-3 w-3" /> Unverified
              </Badge>
            ) : null}
            <span className="ml-auto">Uploaded {format(new Date(row.created_at), "MMM d, yyyy h:mm a")}</span>
            {!isAttached && row.status === "completed" && (
              <AttachToOrderButton
                row={row}
                onAttached={() => {
                  queryClient.invalidateQueries({ queryKey: ["attached-document-paths"] });
                  queryClient.invalidateQueries({ queryKey: ["document-extractions"] });
                }}
              />
            )}
          </div>

          {/* Document Preview */}
          {row.file_path && <DocumentPreview filePath={row.file_path} />}

          {row.error_message && (
            <div className="bg-destructive/10 text-destructive text-sm rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              {row.error_message}
            </div>
          )}

          {Object.keys(displayData).length > 0 && (
            <Collapsible>
              <div className="border rounded-lg p-4">
                <CollapsibleTrigger className="flex w-full items-center justify-between">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {row.is_verified ? "Verified Data" : "Extracted Data"}
                  </h4>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <dl className="space-y-2 mt-3">
                    {Object.entries(labels).map(([key, label]) => {
                      const val = (displayData as any)[key];
                      if (val === null || val === undefined || val === "") return null;
                      return (
                        <div key={key} className="flex justify-between gap-4 py-1.5 border-b border-border/50 last:border-0">
                          <dt className="text-sm text-muted-foreground shrink-0">{label}</dt>
                          <dd className="text-sm font-medium text-foreground text-right">{String(val)}</dd>
                        </div>
                      );
                    })}
                    {confidence > 0 && (
                      <div className="flex justify-between gap-4 py-1.5">
                        <dt className="text-sm text-muted-foreground">Confidence</dt>
                        <dd className={cn(
                          "text-sm font-semibold",
                          confidence >= 0.9 ? "text-primary" : confidence >= 0.7 ? "text-amber-600" : "text-destructive"
                        )}>
                          {Math.round(confidence * 100)}%
                        </dd>
                      </div>
                    )}
                  </dl>
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}